// src/pages/SetupPhoneNumbers.jsx
// Tenant Phone Numbers setup page (after login)
// - Lists all Twilio numbers attached to the current tenant
// - Numbers are ALWAYS created via backend (Twilio API under your main account)
// - Frontend only shows the list and triggers "Generate with Twilio"

import React, { useEffect, useState } from "react";
import { apiGet, apiPost } from "../lib/api";

export default function SetupPhoneNumbers() {
  const [numbers, setNumbers] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  async function loadNumbers() {
    try {
      setLoadingList(true);
      setError("");
      const data = await apiGet("/phonemap"); // backend deve filtrar por tenantId do token
      const list = Array.isArray(data) ? data : data.items || [];
      setNumbers(list);
    } catch (err) {
      console.error("Error loading phone numbers:", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to load phone numbers.";
      setError(msg);
    } finally {
      setLoadingList(false);
    }
  }

  useEffect(() => {
    loadNumbers();
  }, []);

  async function handleGenerate() {
    setError("");
    setSuccess("");

    // Aqui assumimos uma rota backend tipo:
    // POST /phonemap/generate
    // que:
    //  - compra/associa um número Twilio na sua conta master
    //  - cria o registro PhoneMap com tenantId do usuário logado
    //  - retorna o número criado ou a lista atualizada
    try {
      setGenerating(true);
      const data = await apiPost("/phonemap/generate", {});
      const list = Array.isArray(data) ? data : data.items || null;

      if (list) {
        setNumbers(list);
      } else {
        // se backend só retornar "ok", recarrega explicitamente
        await loadNumbers();
      }

      setSuccess("New Twilio number generated and attached to this tenant.");
    } catch (err) {
      console.error("Error generating Twilio number:", err);
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to generate a new Twilio number.";
      setError(msg);
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100">
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <header className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-white">
              Tenant Setup – Phone Numbers
            </h1>
            <p className="mt-1 max-w-2xl text-sm text-slate-300">
              These are the Twilio phone numbers attached to your tenant. All
              numbers are created through the system using your main Twilio
              account. You cannot type numbers manually.
            </p>
          </div>

          <div className="mt-3 sm:mt-0">
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating}
              className="inline-flex items-center justify-center rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-slate-900 shadow hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {generating ? "Generating…" : "Generate number with Twilio"}
            </button>
            <p className="mt-1 text-[11px] text-slate-400 max-w-xs">
              The system will purchase/assign a number via Twilio under your
              account and automatically attach it to this tenant.
            </p>
          </div>
        </header>

        {/* Alerts */}
        {error && (
          <div className="mb-4 rounded-lg border border-red-500/60 bg-red-500/10 px-4 py-2 text-sm text-red-100">
            {error}
          </div>
        )}
        {success && (
          <div className="mb-4 rounded-lg border border-emerald-500/60 bg-emerald-500/10 px-4 py-2 text-sm text-emerald-100">
            {success}
          </div>
        )}

        {/* List */}
        <section className="overflow-hidden rounded-2xl border border-slate-700 bg-slate-900/70 shadow-lg">
          <div className="flex items-center justify-between border-b border-slate-800 px-4 py-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-200">
              Connected Numbers
            </h2>
            <span className="text-xs text-slate-400">
              {loadingList ? "Loading…" : `${numbers.length} number(s)`}
            </span>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-800/70">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-300">
                    Label
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-300">
                    Channel
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-300">
                    Phone Number
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-300">
                    Messaging Service SID
                  </th>
                  <th className="px-4 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-300">
                    Default
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-300">
                    Created at
                  </th>
                </tr>
              </thead>
              <tbody>
                {loadingList && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-6 text-center text-slate-400"
                    >
                      Loading phone numbers…
                    </td>
                  </tr>
                )}

                {!loadingList && numbers.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-6 text-center text-slate-400"
                    >
                      No phone numbers yet. Click{" "}
                      <span className="font-semibold text-slate-100">
                        “Generate number with Twilio”
                      </span>{" "}
                      to create your first one.
                    </td>
                  </tr>
                )}

                {!loadingList &&
                  numbers.map((n) => (
                    <tr
                      key={n._id}
                      className="border-t border-slate-800 hover:bg-slate-800/40"
                    >
                      <td className="px-4 py-2 align-middle text-slate-100">
                        {n.label || "—"}
                      </td>
                      <td className="px-4 py-2 align-middle text-slate-200">
                        <span className="rounded-full bg-slate-800 px-2 py-0.5 text-xs uppercase tracking-wide text-slate-200">
                          {n.channel || "sms"}
                        </span>
                      </td>
                      <td className="px-4 py-2 align-middle text-slate-100">
                        {n.toNumber || n.phoneNumber || "—"}
                      </td>
                      <td className="px-4 py-2 align-middle text-slate-300">
                        <span className="break-all text-[11px]">
                          {n.messagingServiceSid || "—"}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-center align-middle text-slate-100">
                        {n.isDefault ? (
                          <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-300">
                            Default
                          </span>
                        ) : (
                          <span className="text-[11px] text-slate-500">—</span>
                        )}
                      </td>
                      <td className="px-4 py-2 align-middle text-slate-300 text-xs">
                        {n.createdAt
                          ? new Date(n.createdAt).toLocaleString()
                          : "—"}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
